import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FilterableSelect } from '../common/FilterableSelect';
import {
  DESCRIPTOR_CATEGORIES_BY_TYPE,
  useAddDescriptor,
  useDeleteDescriptor,
  useEntity,
  useReorderDescriptors,
  useUpdateDescriptor,
  type EntityDescriptorDto,
  type EntityTypeSlug,
} from '../../api/queries/entities';
import { CharacterStructuredFields } from './CharacterStructuredFields';
import {
  hasStructuredFields,
  STRUCTURED_CATEGORIES,
  CHARACTER_FREEFORM_CATEGORIES,
} from './characterFieldConfig';

interface Props {
  entityId: string;
  entityType: EntityTypeSlug;
  descriptors?: EntityDescriptorDto[];
  onRequestAIGenerate?: () => void;
}

export function EntityDescriptorsEditor({
  entityId,
  entityType,
  descriptors: descriptorsProp,
  onRequestAIGenerate,
}: Props) {
  const entityQuery = useEntity(descriptorsProp ? undefined : entityId);
  const descriptors = descriptorsProp ?? entityQuery.data?.descriptors ?? [];
  const add = useAddDescriptor(entityId);
  const remove = useDeleteDescriptor(entityId);
  const reorder = useReorderDescriptors(entityId);
  const isCharacter = hasStructuredFields(entityType);

  // For free-form section, filter out structured categories
  const freeformDescriptors = isCharacter
    ? descriptors.filter((d) => !STRUCTURED_CATEGORIES.has(d.category))
    : descriptors;

  const freeformCategories = isCharacter
    ? CHARACTER_FREEFORM_CATEGORIES
    : (DESCRIPTOR_CATEGORIES_BY_TYPE[entityType] ?? []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const [category, setCategory] = useState(freeformCategories[0] ?? 'detail');
  const [content, setContent] = useState('');

  const handleAdd = async () => {
    if (!content.trim()) return;
    await add.mutateAsync({ category, content: content.trim() });
    setContent('');
  };

  // Group free-form descriptors by category
  const grouped = new Map<string, EntityDescriptorDto[]>();
  for (const d of freeformDescriptors) {
    const key = d.category.toLowerCase();
    const existing = grouped.get(key) ?? [];
    existing.push(d);
    grouped.set(key, existing);
  }

  const orderedCategories = [
    ...freeformCategories.filter((c) => grouped.has(c)),
    ...Array.from(grouped.keys()).filter(
      (c) => !freeformCategories.includes(c),
    ),
  ];

  const handleDragEnd = (cat: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const items = grouped.get(cat) ?? [];
    const oldIndex = items.findIndex((d) => d.id === active.id);
    const newIndex = items.findIndex((d) => d.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    const allOrdered = orderedCategories.flatMap((c) =>
      c === cat ? reordered : (grouped.get(c) ?? []),
    );
    reorder.mutate(allOrdered.map((d) => d.id));
  };

  return (
    <Stack spacing={2}>
      {/* AI Generate button */}
      {onRequestAIGenerate && (
        <Button
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          onClick={onRequestAIGenerate}
          sx={{ alignSelf: 'flex-start' }}
        >
          Generate with AI
        </Button>
      )}

      {/* Structured fields section (characters only) */}
      {isCharacter && (
        <CharacterStructuredFields
          entityId={entityId}
          descriptors={descriptors}
        />
      )}

      {/* Free-form descriptors section */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {isCharacter ? 'Additional Descriptors' : 'Descriptors'}
        </Typography>

        {/* Add descriptor form */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{ mb: 2 }}
        >
          <FilterableSelect
            options={[
              ...freeformCategories.map((c) => ({ value: c, label: c })),
              { value: '__custom__', label: '(custom\u2026)' },
            ]}
            value={category}
            onChange={(newValue) => setCategory(newValue)}
            label="Category"
            allowEmpty={false}
            size="small"
            sx={{ minWidth: 180 }}
          />
          {category === '__custom__' && (
            <TextField
              size="small"
              label="Custom category"
              onChange={(e) => setCategory(e.target.value.trim().toLowerCase())}
            />
          )}
          <TextField
            fullWidth
            size="small"
            label="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            minRows={2}
          />
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!content.trim() || add.isPending}
          >
            Add
          </Button>
        </Stack>

        {/* Grouped descriptor rows */}
        {orderedCategories.length === 0 && !isCharacter && (
          <Typography variant="caption" color="text.secondary">
            No descriptors yet.
          </Typography>
        )}

        {orderedCategories.map((cat, idx) => {
          const items = grouped.get(cat) ?? [];
          return (
            <Box key={cat}>
              {idx > 0 && <Divider sx={{ my: 1 }} />}
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ textTransform: 'capitalize' }}
              >
                {cat.replace(/_/g, ' ')}
              </Typography>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(e) => handleDragEnd(cat, e)}
              >
                <SortableContext
                  items={items.map((d) => d.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={0.5}>
                    {items.map((d) => (
                      <SortableDescriptorRow
                        key={d.id}
                        entityId={entityId}
                        descriptor={d}
                        onDelete={() => remove.mutate(d.id)}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            </Box>
          );
        })}
      </Paper>
    </Stack>
  );
}

function SortableDescriptorRow({
  entityId,
  descriptor,
  onDelete,
}: {
  entityId: string;
  descriptor: EntityDescriptorDto;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: descriptor.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style}>
      <Stack direction="row" spacing={0.5} alignItems="flex-start">
        <IconButton
          size="small"
          sx={{
            cursor: 'grab',
            mt: 0.5,
            opacity: 0.4,
            '&:hover': { opacity: 1 },
          }}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <DescriptorRow
            entityId={entityId}
            descriptor={descriptor}
            onDelete={onDelete}
          />
        </Box>
      </Stack>
    </div>
  );
}

function DescriptorRow({
  entityId,
  descriptor,
  onDelete,
}: {
  entityId: string;
  descriptor: EntityDescriptorDto;
  onDelete: () => void;
}) {
  const update = useUpdateDescriptor(entityId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(descriptor.content);

  if (!editing) {
    return (
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Typography variant="body2" sx={{ flex: 1 }}>
          {descriptor.content}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setEditing(true)}
          aria-label="edit"
        >
          ✎
        </IconButton>
        <IconButton
          size="small"
          onClick={onDelete}
          aria-label="delete"
          color="error"
        >
          ✕
        </IconButton>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <TextField
        fullWidth
        size="small"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        multiline
      />
      <Button
        size="small"
        variant="contained"
        disabled={!draft.trim()}
        onClick={async () => {
          await update.mutateAsync({
            id: descriptor.id,
            category: descriptor.category,
            content: draft.trim(),
            sequenceOrder: descriptor.sequenceOrder,
          });
          setEditing(false);
        }}
      >
        Save
      </Button>
      <Button size="small" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </Stack>
  );
}
